/** New pre-trip item CATEGORY dialog: pick any tenant + display name + system
 *  name + rendering type, POST, then navigate to the created category. */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as typesApi from '@/api/preTripItemTypes';
import { RENDERING_TYPE_LABELS, type RenderingType } from '@/api/preTripItemTypes';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

const RENDERING_TYPES: RenderingType[] = [1, 2, 3, 4];

export function PreTripItemCategoryCreateDialog({
  open,
  onClose,
  pageTenants,
}: {
  open: boolean;
  onClose: () => void;
  /** The page's operators (D9) — see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [tenant, setTenant, resetTenant] = useCreateDialogTenant(open, pageTenants);
  const [name, setName] = useState('');
  const [systemName, setSystemName] = useState('');
  const [renderingType, setRenderingType] = useState<RenderingType>(1);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    resetTenant();
    setName('');
    setSystemName('');
    setRenderingType(1);
    setError(null);
  };

  // Page tenants: every open starts a fresh form on the page's tenant, so a
  // Cancelled pick (and the fields that hang on it) never carries over.
  useEffect(() => {
    if (open && pageTenants !== undefined) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      typesApi.create({
        tour_operator: tenant!.id,
        name: name.trim(),
        system_name: systemName.trim(),
        rendering_type: renderingType,
      }),
    onSuccess: (type) => {
      snackbar.show('Category created.', 'success');
      qc.invalidateQueries({ queryKey: ['pre_trip_item_types'] });
      reset();
      onClose();
      navigate(`/pre-trip-item-categories/${type.id}`);
    },
    onError: (e) => setError(e instanceof ConsoleApiError ? e.message : 'Create failed.'),
  });

  const canSubmit =
    tenant !== null && name.trim().length > 0 && systemName.trim().length > 0 && !mutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New category</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TenantPicker value={tenant} featured={pageTenants} onChange={setTenant} label="Operator" placeholder="Select operator" />
          <TextField
            label="Display name" size="small" fullWidth value={name} required
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="System name" size="small" fullWidth value={systemName} required
            onChange={(e) => setSystemName(e.target.value)}
          />
          <TextField
            select label="Type" size="small" sx={{ width: 260 }} value={renderingType}
            onChange={(e) => setRenderingType(Number(e.target.value) as RenderingType)}
          >
            {RENDERING_TYPES.map((rt) => (
              <MenuItem key={rt} value={rt}>{RENDERING_TYPE_LABELS[rt]}</MenuItem>
            ))}
          </TextField>
          {error && <Box sx={{ color: 'error.main', fontSize: 13 }}>{error}</Box>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Creating…' : 'Create category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
